import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Ticket, Clock, ExternalLink, ChevronDown, CheckCircle2, Flag, Lock, X, Trophy, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Feed from "@/pages/Feed";
import DateNav, { toISODate } from "@/components/DateNav";

const DISC = { a: "Attelé", m: "Monté", p: "Plat", h: "Haies", s: "Steeple", c: "Cross", o: "Obstacle" };
const PLACE = { "0": "Non placé (10e ou +)", D: "Disqualifié(e)", T: "Tombé", A: "Arrêté", Ret: "Rétrogradé", R: "Rétrogradé" };

function decodeMusique(mus) {
  if (!mus) return [];
  const tokens = mus.match(/\(\d{2,4}\)|[0-9DTARN]+[a-z]/g) || [];
  return tokens.map((t) => {
    const year = t.match(/^\((\d{2,4})\)$/);
    if (year) return { type: "year", label: `Saison 20${year[1].slice(-2)}` };
    const m = t.match(/^([0-9DTARN]+)([a-z])$/);
    if (!m) return { type: "perf", place: t, placeLabel: t, disc: "" };
    const [, place, disc] = m;
    const placeLabel = PLACE[place] || (place === "1" ? "1er" : `${place}e`);
    return { type: "perf", raw: t, place, placeLabel, disc: DISC[disc] || disc, win: place === "1", podium: ["1", "2", "3"].includes(place) };
  });
}

const POINTS_BY_PLACE = [8, 6, 4, 2, 1];

function pointsForRank(rank) {
  return rank && rank >= 1 && rank <= 5 ? POINTS_BY_PLACE[rank - 1] : 0;
}

function placeChip(rank) {
  if (rank === 1) return { label: "1er", cls: "bg-[#FFD700] text-[#04180a]" };
  if (rank === 2) return { label: "2e", cls: "bg-[#C0C0C0] text-[#04180a]" };
  if (rank === 3) return { label: "3e", cls: "bg-[#CD7F32] text-white" };
  return { label: `${rank}e`, cls: "bg-[#1A2B20] text-[#A1B5A8] border border-[#00E676]/15" };
}

function PartantsTable({ course, onPick, onHorse }) {
  const settled = course.status === "settled";
  const arrivee = course.arrivee || [];
  const rankOf = (num) => {
    const i = arrivee.indexOf(num);
    return i === -1 ? null : i + 1;
  };
  let rows = course.partants || [];
  if (settled && arrivee.length) {
    rows = [...rows].sort((a, b) => (rankOf(a.numero) ?? 999) - (rankOf(b.numero) ?? 999));
  }
  return (
    <div className="overflow-x-auto">
      {settled && arrivee.length > 0 && (
        <div className="flex items-center gap-2 text-xs font-bold text-[#39FF14] mb-2" data-testid={`arrivee-order-${course.id}`}>
          <Flag size={12} /> Ordre d'arrivée officiel : <span className="tabular text-white">{arrivee.slice(0, 5).join(" - ")}</span>
        </div>
      )}
      <table className="w-full text-sm" data-testid={`partants-${course.id}`}>
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-[#697C6F] border-b border-[#00E676]/10">
            <th className="px-2 py-2">{settled && arrivee.length ? "Place" : "N°"}</th>
            <th className="px-2 py-2">Cheval</th>
            <th className="px-2 py-2 hidden sm:table-cell">Driver / Jockey</th>
            <th className="px-2 py-2 hidden md:table-cell">S/A</th>
            <th className="px-2 py-2 hidden lg:table-cell">Gains</th>
            <th className="px-2 py-2">Musique</th>
            <th className="px-2 py-2 text-right">Favori</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const picked = course.my_pick === p.numero;
            const rank = settled ? rankOf(p.numero) : null;
            const chip = rank ? placeChip(rank) : null;
            const myPts = picked && settled ? pointsForRank(rank) : 0;
            const np = !!p.non_partant;
            return (
              <tr key={p.numero} className={`border-b border-[#00E676]/5 ${picked ? "bg-[#00E676]/12 border-l-2 border-l-[#39FF14]" : ""} ${np && !settled ? "opacity-50" : ""}`}>
                <td className="px-2 py-2">
                  {settled && arrivee.length ? (
                    <div className="flex items-center gap-1.5">
                      {chip ? (
                        <span className={`tabular min-w-[34px] h-7 px-1.5 grid place-items-center rounded-md font-black text-xs ${chip.cls}`}>{chip.label}</span>
                      ) : (
                        <span className="tabular min-w-[34px] h-7 px-1.5 grid place-items-center rounded-md font-bold text-[10px] bg-[#0F1812] text-[#697C6F] border border-[#00E676]/10">NP</span>
                      )}
                      <span className="tabular text-[11px] text-[#697C6F]">n°{p.numero}</span>
                    </div>
                  ) : (
                    <span className="tabular w-7 h-7 grid place-items-center rounded-md font-black bg-[#1A2B20] text-white">{p.numero}</span>
                  )}
                </td>
                <td className="px-2 py-2 font-semibold">
                  <button onClick={() => onHorse(p)} data-testid={`horse-${course.id}-${p.numero}`} className={`flex items-center gap-2 text-left hover:text-[#39FF14] hover:underline underline-offset-2 transition-colors ${picked ? "text-[#39FF14]" : ""} ${np ? "line-through text-[#697C6F]" : ""}`}>
                    {p.casaque && (
                      <img src={p.casaque} alt="" className="h-6 w-6 rounded object-contain bg-white/90 p-0.5 shrink-0" loading="lazy" />
                    )}
                    {p.cheval}
                  </button>
                </td>
                <td className="px-2 py-2 text-[#A1B5A8] hidden sm:table-cell">{p.jockey}</td>
                <td className="px-2 py-2 text-[#A1B5A8] hidden md:table-cell tabular">{p.sexe_age}</td>
                <td className="px-2 py-2 text-[#A1B5A8] hidden lg:table-cell tabular">{p.gains ? `${p.gains.toLocaleString("fr-FR")} €` : "—"}</td>
                <td className="px-2 py-2 text-[#39FF14] tabular text-xs whitespace-nowrap">{p.musique || "—"}</td>
                <td className="px-2 py-2 text-right">
                  {settled ? (
                    picked ? (
                      <span className={`text-xs font-black px-3 py-1 rounded-full text-black ${myPts > 0 ? "bg-[#FFD700]" : "bg-[#C7CDD1]"}`}>
                        mon choix · +{myPts} pts
                      </span>
                    ) : null
                  ) : np ? (
                    <span data-testid={`np-${course.id}-${p.numero}`} className="text-xs font-bold px-2 py-1 rounded-md bg-[#FFD400]/15 text-[#FFD400] whitespace-nowrap">Non partant</span>
                  ) : course.locked ? (
                    picked ? (
                      <span className="text-xs text-[#39FF14]">mon favori ✓</span>
                    ) : (
                      <span className="text-xs text-[#697C6F] flex items-center gap-1 justify-end"><Lock size={11} /> Fermé</span>
                    )
                  ) : (
                    <button
                      onClick={() => onPick(course.id, p.numero)}
                      data-testid={`fav-${course.id}-${p.numero}`}
                      className={`text-xs font-bold px-3 py-1.5 rounded-md transition-colors ${picked ? "bg-[#39FF14] text-[#04180a]" : "bg-[#1A2B20] text-[#A1B5A8] hover:text-white hover:bg-[#234230]"}`}
                    >
                      {picked ? "Mon favori ✓" : "Choisir"}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Countdown({ lockAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = new Date(lockAt).getTime() - now;
  if (diff <= 0) return null;
  const totalSec = Math.floor(diff / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const soon = diff <= 5 * 60 * 1000;
  let label;
  if (h > 0) label = `${h}h${String(m).padStart(2, "0")}`;
  else if (m > 0) label = `${m} min`;
  else label = `${s}s`;
  return (
    <span
      data-testid="course-countdown"
      className={`text-xs font-bold flex items-center gap-1 px-2 py-1 rounded-md ${soon ? "bg-[#FF5A3C]/15 text-[#FF7A5C] animate-pulse" : "bg-[#FFCC00]/10 text-[#FFCC00]"}`}
    >
      <Clock size={11} /> Ferme dans {label}
    </span>
  );
}

function CourseBlock({ course, onPick, onHorse }) {
  const [open, setOpen] = useState(false);
  const settled = course.status === "settled";
  const myRank = settled && course.my_pick ? ((course.arrivee || []).indexOf(course.my_pick) + 1 || null) : null;
  const myPoints = pointsForRank(myRank);
  return (
    <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] overflow-hidden" data-testid={`game-course-${course.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-[#1A2B20]/50 transition-colors">
        <div className="flex items-center gap-3 min-w-0">
          <span className="tabular shrink-0 px-2.5 py-1 rounded-md bg-[#00E676] text-[#04180a] font-black text-sm">{course.course}</span>
          <div className="min-w-0">
            <div className="font-bold truncate">{course.nom_prix || "Course"}</div>
            <div className="text-xs text-[#697C6F] flex items-center gap-2 flex-wrap">
              {course.heure && <span className="flex items-center gap-1"><Clock size={11} />{course.heure}</span>}
              {course.discipline && <span>{course.discipline}</span>}
              {course.distance && <span className="tabular">{course.distance}m</span>}
              <span className="tabular">{(course.partants || []).length} partants</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {!settled && !course.locked && course.lock_at && <Countdown lockAt={course.lock_at} />}
          {settled ? (
            <div className="flex items-center gap-2">
              {course.my_pick && (
                <span data-testid={`my-points-${course.id}`} className={`text-xs font-black px-3 py-1 rounded-full text-black ${myPoints > 0 ? "bg-[#FFD700]" : "bg-[#C7CDD1]"}`}>
                  n°{course.my_pick} · +{myPoints} pts
                </span>
              )}
              <span className="text-xs font-bold text-[#39FF14] flex items-center gap-1"><Flag size={12} /> {(course.arrivee || []).slice(0, 5).join("-")}</span>
            </div>
          ) : course.my_pick ? (
            <span className="text-xs font-bold text-[#39FF14] flex items-center gap-1"><CheckCircle2 size={13} /> n°{course.my_pick}</span>
          ) : course.locked ? (
            <span className="text-xs font-bold text-[#697C6F] flex items-center gap-1"><Lock size={12} /> fermé</span>
          ) : (
            <span className="text-xs text-[#FFCC00]">à jouer</span>
          )}
          <ChevronDown size={18} className={`text-[#697C6F] transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4">
          {course.programme_url && (
            <a href={course.programme_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#39FF14] mb-3 hover:underline" data-testid={`programme-link-${course.id}`}>
              <ExternalLink size={12} /> Voir le programme complet
            </a>
          )}
          <PartantsTable course={course} onPick={onPick} onHorse={onHorse} />
        </div>
      )}
    </div>
  );
}

export default function Game() {
  const [selDate, setSelDate] = useState(toISODate());
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeReunion, setActiveReunion] = useState(null);
  const [horse, setHorse] = useState(null);

  const load = async (date) => {
    setLoading(true);
    try {
      const res = await api.get("/game/courses", { params: date ? { date } : {} });
      setCourses(res.data);
      const firstR = res.data[0]?.reunion_num ?? null;
      setActiveReunion(firstR);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(selDate);
  }, []);

  const pick = async (courseId, numero) => {
    try {
      await api.post("/game/pick", { game_course_id: courseId, numero });
      setCourses((cs) => cs.map((c) => (c.id === courseId ? { ...c, my_pick: numero } : c)));
      toast.success("Favori enregistré !");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reunions = [];
  const seen = {};
  courses.forEach((c) => {
    if (!seen[c.reunion_num]) {
      seen[c.reunion_num] = true;
      reunions.push({ num: c.reunion_num, label: c.reunion, hippodrome: c.hippodrome });
    }
  });
  const visible = courses.filter((c) => c.reunion_num === activeReunion);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[#39FF14] font-script text-2xl">À vous de jouer</div>
          <h1 className="text-4xl sm:text-5xl font-black flex items-center gap-3">Le Jeu <Ticket className="text-[#39FF14]" /></h1>
          <p className="text-[#A1B5A8] mt-1">Choisissez votre favori dans chaque course. Voici les points selon la place de votre cheval :</p>
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="points-legend">
            {[
              { p: "1er", pts: 8, cls: "from-[#FFD700] to-[#FFB300] text-[#04180a]" },
              { p: "2e", pts: 6, cls: "from-[#D8D8D8] to-[#AFAFAF] text-[#04180a]" },
              { p: "3e", pts: 4, cls: "from-[#E1935A] to-[#CD7F32] text-white" },
              { p: "4e", pts: 2, cls: "from-[#1A2B20] to-[#132018] text-[#39FF14] ring-1 ring-[#00E676]/30" },
              { p: "5e", pts: 1, cls: "from-[#1A2B20] to-[#132018] text-[#39FF14] ring-1 ring-[#00E676]/30" },
            ].map((it) => (
              <div key={it.p} className={`flex items-center gap-1.5 rounded-full pl-1.5 pr-3 py-1 bg-gradient-to-r ${it.cls} shadow-sm`}>
                <span className="min-w-[26px] h-6 grid place-items-center rounded-full bg-black/15 text-xs font-black tabular">{it.p}</span>
                <span className="text-sm font-black tabular">+{it.pts}</span>
                <span className="text-[10px] font-bold uppercase tracking-wide opacity-80">pts</span>
              </div>
            ))}
          </div>
        </div>
        <DateNav value={selDate} onChange={(d) => { setSelDate(d); load(d); }} />
      </div>

      <div className="grid md:grid-cols-[1fr_340px] gap-6 items-start">
        <div>
          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#00E676]" /></div>
          ) : courses.length === 0 ? (
            <div className="text-center py-24 rounded-xl border border-dashed border-[#00E676]/20 bg-[#131F17]/50 text-[#A1B5A8]">
              Aucun programme disponible pour cette date. L'administrateur va bientôt le mettre en ligne.
            </div>
          ) : (
            <>
              <div className="flex gap-2 overflow-x-auto pb-2 mb-5" data-testid="reunion-tabs">
                {reunions.map((r) => (
                  <button key={r.num} onClick={() => setActiveReunion(r.num)} data-testid={`reunion-${r.num}`}
                    className={`shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeReunion === r.num ? "bg-[#00E676] text-[#04180a]" : "bg-[#131F17] text-[#A1B5A8] hover:text-white"}`}>
                    <span className="font-black">{r.label}</span> <span className="opacity-80">{r.hippodrome}</span>
                  </button>
                ))}
              </div>
              <div className="space-y-3">
                {visible.map((c) => <CourseBlock key={c.id} course={c} onPick={pick} onHorse={setHorse} />)}
              </div>
            </>
          )}
        </div>

        <Feed />
      </div>

      <Link
        to="/challenge"
        data-testid="game-to-challenge"
        className="group mt-8 flex items-center justify-between gap-4 rounded-2xl border border-[#FFD700]/25 bg-gradient-to-r from-[#00E676]/10 via-[#131F17] to-[#FFD700]/10 px-6 py-5 hover:border-[#FFD700]/50 hover:shadow-[0_0_28px_rgba(255,215,0,0.18)] transition-all"
      >
        <div className="flex items-center gap-4">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-[#FFD700] to-[#CD7F32] text-[#04180a] shadow-lg">
            <Trophy size={24} />
          </span>
          <div>
            <div className="text-lg font-black text-white">Voir mon classement</div>
            <div className="text-sm text-[#A1B5A8]">Découvrez votre place dans le Classement des turfistes</div>
          </div>
        </div>
        <ArrowRight size={22} className="text-[#39FF14] shrink-0 transition-transform group-hover:translate-x-1" />
      </Link>

      {horse && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 p-4" onClick={() => setHorse(null)}>
          <div className="w-full max-w-lg rounded-2xl border border-[#00E676]/30 bg-[#131F17] overflow-hidden ant-in" onClick={(e) => e.stopPropagation()} data-testid="horse-modal">
            <div className="flex items-start justify-between gap-3 p-5 border-b border-[#00E676]/15 bg-gradient-to-r from-[#00E676]/10 to-transparent">
              <div className="flex items-center gap-3">
                <span className="tabular w-11 h-11 grid place-items-center rounded-lg bg-[#00E676] text-[#04180a] font-black text-lg">{horse.numero}</span>
                {horse.casaque && (
                  <img src={horse.casaque} alt="Casaque" data-testid="horse-casaque" className="h-11 w-11 rounded-lg object-contain bg-white/90 p-0.5 ring-1 ring-[#00E676]/30" />
                )}
                <div>
                  <h3 className="text-xl font-black">{horse.cheval}</h3>
                  <p className="text-sm text-[#A1B5A8]">{horse.jockey}{horse.sexe_age ? ` · ${horse.sexe_age}` : ""}</p>
                </div>
              </div>
              <button onClick={() => setHorse(null)} className="p-1.5 rounded-lg text-[#A1B5A8] hover:text-white hover:bg-[#1A2B20]"><X size={18} /></button>
            </div>

            <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-[#0F1812] p-3">
                  <div className="text-[10px] uppercase tracking-wide text-[#697C6F]">Gains</div>
                  <div className="tabular font-bold">{horse.gains ? `${horse.gains.toLocaleString("fr-FR")} €` : "—"}</div>
                </div>
                <div className="rounded-lg bg-[#0F1812] p-3">
                  <div className="text-[10px] uppercase tracking-wide text-[#697C6F]">Cote</div>
                  <div className="tabular font-bold text-[#39FF14]">{horse.cote ?? "—"}</div>
                </div>
                <div className="rounded-lg bg-[#0F1812] p-3">
                  <div className="text-[10px] uppercase tracking-wide text-[#697C6F]">Poids</div>
                  <div className="tabular font-bold">{horse.poids ? `${horse.poids} kg` : "—"}</div>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-[#697C6F] mb-2">Musique (dernières performances)</div>
                {!horse.musique ? (
                  <p className="text-sm text-[#697C6F]">Aucune performance disponible.</p>
                ) : (
                  <>
                    <div className="tabular text-[#39FF14] font-bold mb-3 break-words">{horse.musique}</div>
                    <div className="flex flex-wrap gap-2" data-testid="musique-decoded">
                      {decodeMusique(horse.musique).map((p, i) =>
                        p.type === "year" ? (
                          <span key={i} className="w-full text-[11px] font-bold text-[#697C6F] mt-1">{p.label}</span>
                        ) : (
                          <div key={i} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${p.win ? "bg-[#39FF14]/15 border border-[#39FF14]/40" : p.podium ? "bg-[#00E676]/10 border border-[#00E676]/25" : "bg-[#0F1812] border border-[#00E676]/10"}`}>
                            <span className={`tabular font-black ${p.win ? "text-[#39FF14]" : "text-white"}`}>{p.place}</span>
                            <span className="text-[#A1B5A8] text-xs">{p.placeLabel}{p.disc ? ` · ${p.disc}` : ""}</span>
                          </div>
                        )
                      )}
                    </div>
                    <p className="mt-3 text-[11px] text-[#697C6F] leading-relaxed">
                      Lecture de la plus récente à la plus ancienne. 1 = 1er, 0 = non placé (10e+), D = disqualifié, T = tombé, A = arrêté. Discipline : a = attelé, m = monté, p = plat, h = haies, s = steeple.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
